// Shared UI logic for the native (Capacitor Android) V2 printing modal tree
// (Printers tab, setup wizard, edit modal, Help tab). Naming symmetric with
// use-printing-v2-ui.ts, but computed over the NATIVE data model: name-based
// assignments + saved network printers in usePrintingStore — never the
// bridge's logical printers.
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { EscPosPrinter, NetworkDeviceInfo } from '@/lib/capacitor/EscPosPrinter';
import {
  INetworkPrinterConfig,
  usePrinting,
  usePrintingStore,
} from '@/composition/use-printing';
import {
  INativePrintingStrategy,
  isNativePrintingStrategy,
} from '@/composition/printing/printing-strategy-native';
import { useNativePrintTests } from '@/composition/printing/use-native-print-tests';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { IPrinter } from '@/interfaces/IPrinter';
import { IPrintJob } from '@/interfaces/printing.interfaces';
import { IPrintingSettings } from '@/interfaces/IPrintingSettings';

/**
 * Network-scan connect timeout. The plugin's native default (500 ms) misses
 * real printers on jittery Wi-Fi (a Zebra was missed by scan but reachable
 * through the 4 s manual probe); every scan MUST go through scanNetwork so
 * this stays the single source of truth.
 */
export const NETWORK_SCAN_TIMEOUT_MS = 2000;

export type TNativeProbeState = 'probing' | 'online' | 'offline';

/**
 * Test format resolution for a printer (bug fix for the "Zebra trap"):
 * 'ambiguous' = unassigned — the UI must ask the user (ticket vs etiqueta)
 * instead of blind-sending ESC/POS that a ZPL-only printer silently eats.
 */
export type TNativeTestKind = 'escpos' | 'zpl' | 'both' | 'ambiguous';

export type TNativeDestination = 'ticket' | 'sticker' | 'comanda';

/** Help-tab guided flows (Printers-tab offline hint deep-links into one). */
export type TNativeHelpFlow = 'noprint' | 'symbols' | 'cut' | 'bt' | 'network';

/** One checked chip in the assignment picker (wizard Assign / edit modal). */
export interface INativeDestinationSelection {
  destination: TNativeDestination;
  stationId?: number;
}

export interface INativeAssignment {
  key: string;
  destination: TNativeDestination;
  stationId?: number;
  /** Station name for comanda; empty for ticket/sticker (label via i18n). */
  label: string;
}

export interface INativePrinterCard {
  /** Stable render key (name is the assignment identity natively). */
  key: string;
  name: string;
  /** null for ghosts: only the assignment name is known, not the transport. */
  transport: IPrinter['type'] | null;
  /** Resolvable live printer (discovered usb/bt or saved tcp); null = ghost. */
  printer: IPrinter | null;
  /** Saved config when this card is a saved network printer. */
  networkConfig: INetworkPrinterConfig | null;
  /** Assigned-but-not-discovered/saved: renders as "No detectada". */
  isGhost: boolean;
  assignments: INativeAssignment[];
  /** Network printers only (probe chips); null for usb/bt/ghost. */
  probeState: TNativeProbeState | null;
  /** DLE-EOT PAPER_OUT flag from the last successful probe (tcp only). */
  paperOut: boolean;
  lastJob: IPrintJob | null;
}

// Canonical implementation lifted to the shared print lib (the desktop
// wizard uses it too); re-exported so existing imports keep working.
export { parseHostPort } from '@/lib/print/host-port';
export type { TParseHostPortResult } from '@/lib/print/host-port';

// ---------------------------------------------------------------------------
// Pure card-presentation helpers (native analogue of the desktop
// printerStatusUi/transportLabelKey pair): shared by V2NativePrinterCard and
// the Help-tab diagnostics block.
// ---------------------------------------------------------------------------

export interface INativeCardStatusUi {
  color: 'success' | 'error' | 'info' | 'neutral';
  labelKey: string;
}

export function nativeTransportLabelKey(
  transport: IPrinter['type'] | null
): string {
  if (transport === 'usb') return 'printingV2.transportUsb';
  if (transport === 'bluetooth') return 'printingV2.transportBluetooth';
  return 'printingV2.transportNetwork';
}

/**
 * Honest per-transport semantics: network chips come from real probes;
 * USB "Conectada" = enumerated by the OS; BT "Vinculada" = paired, which
 * is NOT proof of reachability; ghost "No detectada" (may just mean "not
 * listed passively" for BT — the copy must not read as broken). Null for a
 * tcp card that hasn't been probed yet (no chip beats a made-up one).
 */
export function nativeCardStatusUi(
  card: INativePrinterCard
): INativeCardStatusUi | null {
  if (card.isGhost) {
    return { color: 'neutral', labelKey: 'printingV2.nativeStatusMissing' };
  }
  if (card.transport === 'tcp') {
    if (card.probeState === 'probing') {
      return { color: 'neutral', labelKey: 'printingV2.nativeProbing' };
    }
    if (card.probeState === 'online') {
      return { color: 'success', labelKey: 'printingV2.nativeOnline' };
    }
    if (card.probeState === 'offline') {
      return { color: 'error', labelKey: 'printingV2.nativeOffline' };
    }
    return null;
  }
  if (card.transport === 'usb') {
    return { color: 'success', labelKey: 'printingV2.nativeStatusDetected' };
  }
  return { color: 'info', labelKey: 'printingV2.nativeStatusPaired' };
}

// ---------------------------------------------------------------------------
// MODULE scope on purpose (same rationale as testingPrinterId): the modal's
// tabs and the wizard are v-if-mounted, so probe chips and the discovery
// flag must survive tab switches — per-instance refs would reset and re-run
// probes/flash ghosts on every revisit.
// ---------------------------------------------------------------------------

const probeStateByName = ref<Record<string, TNativeProbeState>>({});
/**
 * PAPER_OUT flag per printer name, written ONLY by probes that got a DLE-EOT
 * answer (an unreachable/silent printer says nothing about paper — its stale
 * entry is dropped rather than flipped).
 */
const paperOutByName = ref<Record<string, boolean>>({});
/**
 * True once a reloadAllPrinters pass has completed this session. Ghost cards
 * are only derived after it: before discovery, `printers` is null/stale and
 * every assigned printer would flash as a ghost.
 */
const discoveryDone = ref(false);
const reloading = ref(false);
/** In-flight reload, so an interactive request can join/upgrade it. */
let reloadInFlight: Promise<void> | null = null;
let reloadInFlightInteractive = false;

export function useNativePrintingUi() {
  const printing = usePrinting();
  const store = usePrintingStore();
  const restaurantStore = useRestaurantStore();
  const tests = useNativePrintTests();
  const { networkPrinters, lastPrintJobs } = storeToRefs(store);

  // #region strategy access (guarded — desktop browsers run the plugin
  // strategy, where native accessors don't exist; guarding is what makes
  // browser testing and web graceful degradation viable)

  function nativeStrategyOrNull(): INativePrintingStrategy | null {
    const strategy = store.getStrategy();
    return isNativePrintingStrategy(strategy) ? strategy : null;
  }

  // #endregion

  // #region discovery

  /**
   * Interactive=false must stay prompt-free (passive BT listing): tabs are
   * v-if-mounted, so an interactive mount would re-prompt a user who
   * declined Bluetooth on EVERY revisit.
   */
  async function reload(interactive: boolean): Promise<void> {
    if (reloadInFlight) {
      // Join a pass that already satisfies the request. An INTERACTIVE
      // request during a passive pass must NOT be dropped (it's the only
      // path allowed to raise the BT permission prompt, and the gesture
      // must be honored): wait for the passive pass, then run for real.
      if (!interactive || reloadInFlightInteractive) return reloadInFlight;
      await reloadInFlight;
      return reload(true);
    }

    reloadInFlightInteractive = interactive;
    reloading.value = true;
    reloadInFlight = (async () => {
      try {
        await printing.reloadAllPrinters({ interactive });
      } finally {
        reloading.value = false;
        discoveryDone.value = true;
        reloadInFlight = null;
      }
    })();
    return reloadInFlight;
  }

  /**
   * The ONLY network-scan entry point: always passes the JS-side timeout
   * (the native 500 ms default misses printers — see NETWORK_SCAN_TIMEOUT_MS).
   */
  async function scanNetwork(): Promise<NetworkDeviceInfo[]> {
    const result = await EscPosPrinter.getNetworkPrinterDevices({
      timeoutMs: NETWORK_SCAN_TIMEOUT_MS,
    });
    return result.devices;
  }

  // #endregion

  // #region network probes (modal open, add, test preflight — no polling)

  function networkAddress(config: INetworkPrinterConfig): string {
    return `${config.host}:${config.port}`;
  }

  /**
   * Probes one saved printer through the STRATEGY (serialized against real
   * jobs, feeds the health map) and refreshes the online chip + the stored
   * supportsDleEot flag.
   *
   * Upgrade-only DLE EOT: a silent probe is ambiguous (late reply on jittery
   * Wi-Fi), so it never unlearns proven support — a stale true is harmless
   * (post-send status check treats silence as success).
   *
   * Staleness guard: results are dropped when the saved entry was renamed or
   * re-addressed mid-probe — a late old-address reply must not taint a
   * freshly reset supportsDleEot or write chips under a dead name.
   */
  async function probeOne(config: INetworkPrinterConfig): Promise<boolean> {
    const strategy = nativeStrategyOrNull();
    if (!strategy) {
      probeStateByName.value = {
        ...probeStateByName.value,
        [config.name]: 'offline',
      };
      return false;
    }
    probeStateByName.value = {
      ...probeStateByName.value,
      [config.name]: 'probing',
    };
    return runProbe(strategy, config);
  }

  /** Probe body without the 'probing' seed write (probeAll seeds in bulk). */
  async function runProbe(
    strategy: INativePrintingStrategy,
    config: INetworkPrinterConfig
  ): Promise<boolean> {
    const name = config.name;
    const address = networkAddress(config);
    try {
      const result = await strategy.probeNetworkPrinter(address);

      const current = networkPrinters.value.find((np) => np.name === name);
      if (!current || networkAddress(current) !== address) return false;

      if (result.reachable && result.supportsDleEot) {
        store.setNetworkPrinterDleEot(name, true);
      }
      probeStateByName.value = {
        ...probeStateByName.value,
        [name]: result.reachable ? 'online' : 'offline',
      };
      // statusDetail only exists when the printer ANSWERED DLE EOT; absent
      // detail (silent/unreachable) is no information → drop any stale flag.
      const nextPaper = { ...paperOutByName.value };
      if (result.reachable && result.statusDetail) {
        nextPaper[name] = result.statusDetail.includes('PAPER_OUT');
      } else {
        delete nextPaper[name];
      }
      paperOutByName.value = nextPaper;
      return result.reachable;
    } catch (e) {
      console.error('[NativePrinting] probe failed', e);
      // Same staleness guard as the success path: a late failure for a
      // renamed/re-addressed entry must not write under a dead name.
      const current = networkPrinters.value.find((np) => np.name === name);
      if (!current || networkAddress(current) !== address) return false;
      probeStateByName.value = {
        ...probeStateByName.value,
        [name]: 'offline',
      };
      return false;
    }
  }

  /**
   * Chips are seeded in ONE write (and results land as they resolve): a
   * per-printer synchronous write burst re-renders the whole card grid once
   * per write, and inside vue-js-modal's auto-height render feedback that
   * trips Vue's circular-update breaker — which aborts the flush and can
   * strand the modal's enter/leave transitions (observed stuck-open bug).
   */
  function probeAll(): void {
    const configs = networkPrinters.value;
    if (!configs.length) return;

    const strategy = nativeStrategyOrNull();
    if (!strategy) {
      // No native strategy (desktop browser / web degradation): probes can't
      // run, mark everything offline in a single write.
      const next = { ...probeStateByName.value };
      for (const config of configs) next[config.name] = 'offline';
      probeStateByName.value = next;
      return;
    }

    const seeded = { ...probeStateByName.value };
    for (const config of configs) seeded[config.name] = 'probing';
    probeStateByName.value = seeded;

    void Promise.allSettled(configs.map((c) => runProbe(strategy, c)));
  }

  /**
   * Edit-save rename path: re-keys the chip so the card keeps its state.
   * A 'probing' value is DROPPED instead of carried: its in-flight probe
   * will discard its own result (staleness guard keys by the old name), so
   * carrying it would strand the new card on a forever-spinner.
   */
  function renameProbeState(oldName: string, newName: string): void {
    if (oldName === newName) return;
    const next = { ...probeStateByName.value };
    if (!(oldName in next)) return;
    if (next[oldName] !== 'probing') {
      next[newName] = next[oldName];
    }
    delete next[oldName];
    probeStateByName.value = next;

    if (oldName in paperOutByName.value) {
      const nextPaper = { ...paperOutByName.value };
      nextPaper[newName] = nextPaper[oldName];
      delete nextPaper[oldName];
      paperOutByName.value = nextPaper;
    }
  }

  function removeProbeState(name: string): void {
    if (name in paperOutByName.value) {
      const nextPaper = { ...paperOutByName.value };
      delete nextPaper[name];
      paperOutByName.value = nextPaper;
    }
    if (!(name in probeStateByName.value)) return;
    const next = { ...probeStateByName.value };
    delete next[name];
    probeStateByName.value = next;
  }

  // #endregion

  // #region assignments (union of live refs + persisted names)

  /**
   * Assignment badges per printer NAME, from the union of live refs and
   * persisted names: a post-restart ghost (persisted name retained, live ref
   * null) must still show its badges, and a just-assigned printer must show
   * them before the next persist tick.
   */
  const assignmentsByName = computed<Map<string, INativeAssignment[]>>(() => {
    const map = new Map<string, INativeAssignment[]>();
    const persisted = store.persistedAssignmentNames;

    function add(
      name: string | null | undefined,
      assignment: INativeAssignment
    ) {
      if (!name) return;
      const list = map.get(name) ?? [];
      if (list.some((a) => a.key === assignment.key)) return;
      list.push(assignment);
      map.set(name, list);
    }

    const ticket: INativeAssignment = {
      key: 'ticket',
      destination: 'ticket',
      label: '',
    };
    add(printing.ticketPrinter.value?.name, ticket);
    add(persisted.ticketPrinterName, ticket);

    const sticker: INativeAssignment = {
      key: 'sticker',
      destination: 'sticker',
      label: '',
    };
    add(printing.stickerPrinter.value?.name, sticker);
    add(persisted.stickerPrinterName, sticker);

    for (const station of restaurantStore.printStations) {
      const assignment: INativeAssignment = {
        key: `comanda:${station.stationId}`,
        destination: 'comanda',
        stationId: station.stationId,
        label: station.name,
      };
      add(
        printing.comandaPrintersByStation.value[station.stationId]?.name,
        assignment
      );
      add(
        persisted.comandaPrinterNamesByStation[station.stationId],
        assignment
      );
    }

    return map;
  });

  /**
   * CURRENT holder name of a destination (live ref, falling back to the
   * persisted name so undiscovered ghosts still produce "Reemplaza a X"
   * warnings). Single-name resolution — used by the assignment picker.
   */
  function assignedNameOf(
    destination: 'ticket' | 'sticker' | 'comanda',
    stationId?: number
  ): string | null {
    const persisted = store.persistedAssignmentNames;
    if (destination === 'ticket') {
      return (
        printing.ticketPrinter.value?.name ??
        persisted.ticketPrinterName ??
        null
      );
    }
    if (destination === 'sticker') {
      return (
        printing.stickerPrinter.value?.name ??
        persisted.stickerPrinterName ??
        null
      );
    }
    if (stationId === undefined) return null;
    return (
      printing.comandaPrintersByStation.value[stationId]?.name ??
      persisted.comandaPrinterNamesByStation[stationId] ??
      null
    );
  }

  /**
   * Full taken-name set for name validation (wizard Assign, edit rename):
   * saved network + discovered + persisted assignment names — matching an
   * undiscovered ghost's name would silently take over its assignments via
   * first-match-by-name re-resolution.
   */
  const takenPrinterNames = computed<Set<string>>(() => {
    const names = new Set<string>([
      ...networkPrinters.value.map((np) => np.name),
      ...(printing.printers.value ?? []).map((p) => p.name),
    ]);
    const persisted = store.persistedAssignmentNames;
    for (const name of [
      persisted.ticketPrinterName,
      persisted.stickerPrinterName,
      ...Object.values(persisted.comandaPrinterNamesByStation),
    ]) {
      if (name) names.add(name);
    }
    return names;
  });

  // #endregion

  // #region merged card model

  function lastJobOf(name: string): IPrintJob | null {
    return lastPrintJobs.value.find((job) => job.printerName === name) ?? null;
  }

  /**
   * Cards = discovered usb/bt ∪ saved network ∪ ghost assignment names.
   * Sorted: assigned (including ghosts) first, then saved network, then
   * unassigned detected; name ascending within each group (deterministic).
   */
  const printerCards = computed<INativePrinterCard[]>(() => {
    const cards = new Map<string, INativePrinterCard>();

    // Saved network printers first: they're the durable entities, and a tcp
    // entry in the discovered list is just their projection.
    for (const config of networkPrinters.value) {
      cards.set(config.name, {
        key: `tcp:${config.name}`,
        name: config.name,
        transport: 'tcp',
        printer: {
          type: 'tcp',
          id: networkAddress(config),
          name: config.name,
        },
        networkConfig: config,
        isGhost: false,
        assignments: assignmentsByName.value.get(config.name) ?? [],
        probeState: probeStateByName.value[config.name] ?? null,
        paperOut: paperOutByName.value[config.name] === true,
        lastJob: lastJobOf(config.name),
      });
    }

    for (const printer of printing.printers.value ?? []) {
      if (printer.type === 'tcp') continue; // projection of a saved config
      // Name collision with a saved network printer (a usb/bt device plugged
      // in AFTER the config was saved bypasses every add-time name guard):
      // keep the network card — it's the manageable one (Edit/Remove), and
      // renaming it from there resolves the collision.
      if (cards.has(printer.name)) continue;
      cards.set(printer.name, {
        key: `${printer.type}:${printer.id}`,
        name: printer.name,
        transport: printer.type,
        printer,
        networkConfig: null,
        isGhost: false,
        assignments: assignmentsByName.value.get(printer.name) ?? [],
        probeState: null,
        paperOut: false,
        lastJob: lastJobOf(printer.name),
      });
    }

    // Ghosts: assigned names with no discovered/saved printer. Only after
    // the discovery pass — before it, every assigned usb/bt printer would
    // flash as a ghost. (UI copy caveat: a BT ghost may just mean "not
    // listed passively" — passive listing returns [] without permission.)
    if (discoveryDone.value) {
      for (const [name, assignments] of assignmentsByName.value) {
        if (cards.has(name)) continue;
        cards.set(name, {
          key: `ghost:${name}`,
          name,
          transport: null,
          printer: null,
          networkConfig: null,
          isGhost: true,
          assignments,
          probeState: null,
          paperOut: false,
          lastJob: lastJobOf(name),
        });
      }
    }

    function groupOf(card: INativePrinterCard): number {
      if (card.assignments.length > 0) return 0;
      if (card.networkConfig) return 1;
      return 2;
    }

    return [...cards.values()].sort((a, b) => {
      const groupDiff = groupOf(a) - groupOf(b);
      if (groupDiff !== 0) return groupDiff;
      return a.name.localeCompare(b.name);
    });
  });

  const cardsByName = computed<Map<string, INativePrinterCard>>(() => {
    const map = new Map<string, INativePrinterCard>();
    for (const card of printerCards.value) map.set(card.name, card);
    return map;
  });

  /**
   * Slot health chip (desktop Preferences chipOf blueprint over the native
   * card model), worst-first: error "Sin conexión" (ghost / tcp probe
   * offline / paper-out — same folding as desktop), warning "Sin impresora
   * asignada", info "Se recomienda una impresora de etiquetas" (sticker slot
   * holding a non-zpl printer), else success OK. Pre-discovery (and
   * mid-probe) it reads "Verificando…" instead of flashing a false offline.
   * Shared by the Preferences tab rows and the Help-tab diagnostics.
   */
  function slotHealthChipOf(
    destination: TNativeDestination,
    assignedName: string | null
  ): {
    color: 'success' | 'error' | 'warning' | 'info' | 'neutral';
    labelKey: string;
  } {
    if (!assignedName) {
      return { color: 'warning', labelKey: 'printingV2.healthUnassigned' };
    }
    const card = cardsByName.value.get(assignedName) ?? null;
    if (!card || card.probeState === 'probing') {
      return card || !discoveryDone.value
        ? { color: 'neutral', labelKey: 'printingV2.nativeProbing' }
        : { color: 'error', labelKey: 'printingV2.healthOffline' };
    }
    // A saved network printer that was never probed this session must not
    // claim OK — reachability is simply unknown (the modal shell kicks
    // probeAll on open, so this is a transient pre-seed state).
    if (card.networkConfig && card.probeState === null) {
      return { color: 'neutral', labelKey: 'printingV2.nativeProbing' };
    }
    if (card.isGhost || card.probeState === 'offline' || card.paperOut) {
      return { color: 'error', labelKey: 'printingV2.healthOffline' };
    }
    if (
      destination === 'sticker' &&
      store.printerSettings[assignedName]?.type !== 'zpl'
    ) {
      return { color: 'info', labelKey: 'printingV2.healthStickerFormat' };
    }
    return { color: 'success', labelKey: 'printingV2.healthOk' };
  }

  // #endregion

  // #region tests (honest format dispatch — bug fix for the Zebra trap)

  /**
   * ESC/POS settings for a printer test: a comanda-only printer tests with
   * the comanda codepage (legacy-modal parity); everything else uses the
   * ticket settings.
   */
  function settingsForPrinter(printer: IPrinter): IPrintingSettings {
    const assignments = assignmentsByName.value.get(printer.name) ?? [];
    const isTicket = assignments.some((a) => a.destination === 'ticket');
    const isComanda = assignments.some((a) => a.destination === 'comanda');
    return !isTicket && isComanda
      ? printing.comandaPrintingSettings.value
      : printing.ticketPrintingSettings.value;
  }

  function resolveTestKind(printer: IPrinter): TNativeTestKind {
    const assignments = assignmentsByName.value.get(printer.name) ?? [];
    const isSticker = assignments.some((a) => a.destination === 'sticker');
    const isEscPos = assignments.some(
      (a) => a.destination === 'ticket' || a.destination === 'comanda'
    );
    if (isSticker && isEscPos) return 'both';
    if (isSticker) return 'zpl';
    if (isEscPos) return 'escpos';
    return 'ambiguous';
  }

  /**
   * Assignment-aware test dispatch. For 'ambiguous' (unassigned) printers
   * the caller MUST pass kindOverride from an explicit user choice ("Probar
   * como ticket" / "como etiqueta") — a blind default would re-create the
   * dishonest-test bug this replaces.
   */
  async function runPrinterTest(
    printer: IPrinter,
    kindOverride?: 'escpos' | 'zpl',
    preflight?: () => Promise<boolean>
  ): Promise<void> {
    const kind = kindOverride ?? resolveTestKind(printer);
    if (kind === 'zpl') {
      await tests.testSticker(printer, preflight);
    } else if (kind === 'both') {
      await tests.testBoth(printer, settingsForPrinter(printer), preflight);
    } else {
      if (kind === 'ambiguous') {
        console.warn(
          '[NativePrinting] ambiguous test without kindOverride — defaulting to escpos'
        );
      }
      await tests.testEscPos(printer, settingsForPrinter(printer), preflight);
    }
  }

  // #endregion

  return {
    printing,
    store,
    restaurantStore,
    tests,
    networkPrinters,
    // discovery
    reloading,
    discoveryDone,
    reload,
    scanNetwork,
    // probes
    probeStateByName,
    networkAddress,
    probeOne,
    probeAll,
    renameProbeState,
    removeProbeState,
    nativeStrategyOrNull,
    // assignments + cards
    assignmentsByName,
    assignedNameOf,
    takenPrinterNames,
    printerCards,
    cardsByName,
    slotHealthChipOf,
    // tests
    settingsForPrinter,
    resolveTestKind,
    runPrinterTest,
  };
}
